import { defineStore } from 'pinia';
import moment from 'moment';
import { getClient } from '~/util/awclient';
import { getDefaultClasses, cleanCategory } from '~/util/classes';
import { defaultViews } from '~/stores/views';
import { isEqual } from 'lodash';
import { i18n, isAppLocale, setAppLocale } from '~/i18n';
function jsonEq(a, b) {
    try {
        const jsonA = JSON.parse(JSON.stringify(a));
        const jsonB = JSON.parse(JSON.stringify(b));
        return isEqual(jsonA, jsonB);
    }
    catch (e) {
        // Don't abort the whole settings save if one key cannot be serialized
        // (circular Vue objects, etc.). Treat as "not equal" so we still attempt POST.
        console.error('jsonEq failed', e);
        return false;
    }
}
let settingsLoadPromise = null;
// Backoffs for NewReleaseNotification
export const SHORT_BACKOFF_PERIOD = 24 * 60 * 60;
export const LONG_BACKOFF_PERIOD = 5 * 24 * 60 * 60;
// Initial wait period for UserSatisfactionPoll
export const INITIAL_WAIT_PERIOD = 7 * 24 * 60 * 60;
// Settings that no longer exist. The server has no delete endpoint, so stale values
// may still be stored; skip them on load so they don't get patched back into state.
const REMOVED_KEYS = new Set(['showYearly', 'useMultidevice']);
export const useSettingsStore = defineStore('settings', {
    state: () => ({
        initialTimestamp: moment(),
        startOfDay: '04:00',
        startOfWeek: 'Monday',
        durationDefault: 4 * 60 * 60,
        useColorFallback: false,
        landingpage: '/home',
        theme: 'auto',
        locale: 'en',
        newReleaseCheckData: {
            isEnabled: true,
            nextCheckTime: moment().add(SHORT_BACKOFF_PERIOD, 'seconds'),
            howOftenToCheck: SHORT_BACKOFF_PERIOD,
            timesChecked: 0,
        },
        userSatisfactionPollData: {
            isEnabled: true,
            nextPollTime: moment().add(INITIAL_WAIT_PERIOD, 'seconds'),
            timesPollIsShown: 0,
        },
        uncategorizedNotificationData: {
            isEnabled: true,
            minTotalSeconds: 60 * 60, // 1 hour
            minRatio: 0.3, // 30%
        },
        always_active_pattern: '',
        privacy_filters: [],
        classes: getDefaultClasses(),
        category_sets: [],
        active_set_ids: ['default'],
        views: defaultViews,
        saved_queries: [],
        // Developer settings
        // NOTE: PRODUCTION might be undefined (in tests, for example)
        devmode: typeof PRODUCTION === 'undefined' ? true : !PRODUCTION,
        requestTimeout: 30,
        hideUnsupportedVisualizations: false,
        _loaded: false,
        _storedKeys: [],
    }),
    getters: {
        loaded(state) {
            return state._loaded;
        },
        /** Whether the user has a stored categorization of their own (as opposed to defaults). */
        hasStoredCategories(state) {
            return state._storedKeys.includes('classes') || state._storedKeys.includes('category_sets');
        },
    },
    actions: {
        async ensureLoaded() {
            if (this.loaded) {
                return;
            }
            if (!settingsLoadPromise) {
                settingsLoadPromise = this.load().finally(() => {
                    settingsLoadPromise = null;
                });
            }
            await settingsLoadPromise;
        },
        async load({ save } = {}) {
            if (typeof localStorage === 'undefined') {
                console.error('localStorage is not supported');
                return;
            }
            const client = getClient();
            // Fetch from server, fall back to localStorage
            const server_settings = await client.get_settings();
            // Build a unified map: server value wins, localStorage is fallback.
            // Skip keys that are missing from BOTH sources — otherwise `null` from
            // localStorage.getItem overrides the defaults defined in `state()`.
            const storage = {};
            const used = new Set();
            // 1. Server settings take priority
            for (const key of Object.keys(server_settings)) {
                if (key.startsWith('_') || REMOVED_KEYS.has(key))
                    continue;
                if (key === 'locale' && !isAppLocale(server_settings[key])) {
                    console.warn('Ignoring invalid locale from server:', server_settings[key]);
                    continue;
                }
                storage[key] = server_settings[key];
                used.add(key);
            }
            // 2. localStorage fills in gaps, but skip missing keys (null)
            for (const key of Object.keys(localStorage)) {
                if (key.startsWith('_') || REMOVED_KEYS.has(key) || used.has(key))
                    continue;
                const raw = localStorage.getItem(key);
                if (raw === null || raw === 'null')
                    continue; // key absent or stored as null → keep state() default
                // Keys ending with 'Data' are JSON-serialized objects in localStorage
                const isJsonKey = key.endsWith('Data') ||
                    key == 'views' ||
                    key == 'classes' ||
                    key == 'category_sets' ||
                    key == 'active_set_ids' ||
                    key == 'saved_queries';
                try {
                    if (isJsonKey) {
                        let parsed = JSON.parse(raw);
                        if (key == 'classes') {
                            parsed = parsed.map(cleanCategory);
                        }
                        storage[key] = parsed;
                    }
                    else if (raw === 'true' || raw === 'false') {
                        storage[key] = raw === 'true';
                    }
                    else if (key === 'locale') {
                        if (isAppLocale(raw)) {
                            storage[key] = raw;
                        }
                        else {
                            console.warn('Ignoring invalid locale from storage:', raw);
                        }
                    }
                    else {
                        storage[key] = raw;
                    }
                }
                catch (e) {
                    console.error('failed to parse', key, raw, e);
                }
            }
            this.$patch({ ...storage, _loaded: true, _storedKeys: Object.keys(storage) });
            const localeFromServer = 'locale' in server_settings;
            const localeFromLocalStorage = localStorage.getItem('locale') != null;
            if ((localeFromServer || localeFromLocalStorage) && isAppLocale(this.locale)) {
                setAppLocale(this.locale);
            }
            else if (isAppLocale(i18n.locale)) {
                this.$patch({ locale: i18n.locale });
            }
            // Since `requestTimeout` is used to initialize the client, we need to set it again
            // https://github.com/ActivityWatch/activitywatch/issues/979
            client.req.defaults.timeout = this.requestTimeout * 1000;
            if (save) {
                await this.save();
            }
        },
        async save() {
            // Important check, to avoid saving settings before they are loaded (potentially overwriting them with defaults)
            if (!this.loaded) {
                console.error('Settings not loaded, not saving');
                return;
            }
            // We want to avoid saving to localStorage to not accidentally mess up pre-migration data
            // For example, if the user is using several browsers, and opened in their non-main browser on first run after upgrade.
            const saveToLocalStorage = false;
            // Save to localStorage and backend
            // NOTE: localStorage deprecated, will be removed in future
            const client = getClient();
            // Fetch current settings from server
            const server_settings = await client.get_settings();
            // Save settings
            for (const key of Object.keys(this.$state)) {
                // Skip keys starting with underscore, as they are local to the vuex store.
                if (key.startsWith('_')) {
                    continue;
                }
                const value = this.$state[key];
                // Save to localStorage
                // NOTE: we always save the theme and landingpage to localStorage, since they are used before the settings are loaded
                if (saveToLocalStorage || key == 'theme' || key == 'landingpage') {
                    if (typeof value === 'object') {
                        localStorage.setItem(key, JSON.stringify(value));
                    }
                    else {
                        localStorage.setItem(key, value);
                    }
                }
                // Save changed settings to backend
                if (server_settings[key] === undefined || !jsonEq(server_settings[key], value)) {
                    if (server_settings[key] === undefined && value === false) {
                        // Skip saving settings that are set to false and not already saved on the server
                        continue;
                    }
                    console.log('Saving', { [key]: value });
                    //console.log('Was:', server_settings[key]);
                    //console.log('Now:', value);
                    await client.req.post('/0/settings/' + key, value, {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });
                }
            }
            // After save, reload
            await this.load({ save: false });
        },
        async update(new_state) {
            console.log('Updating state', new_state);
            await this.ensureLoaded();
            this.$patch(new_state);
            await this.save();
        },
    },
});
